import React from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from '../../SvgIcon';
import { DrawerBottom } from '../../drawer';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @returns {JSX.Element} Component.
 */
export const SelectMobile = ({
    label,
    items,
    itemToString,
    initialSelectedItem,
    activeItem,
    onSelectedItemChange
}) => {
    const {
        isOpen,
        openMenu,
        closeMenu,
        selectedItem,
        getToggleButtonProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        getLabelProps
    } = useSelect({
        items,
        initialSelectedItem: initialSelectedItem,
        selectedItem: activeItem,
        // @ts-ignore
        onSelectedItemChange: onSelectedItemChange
    });

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) => (itemToString ? itemToString(item) : item);

    return (
        <>
            <div className="select">
                <button
                    type="button"
                    className="select__display"
                    onClick={() => openMenu()}
                >
                    <label className="select__label">{label}</label>

                    <span className="select__current">
                        <span className="select__current-text">
                            {getItemText(selectedItem) || ''}
                        </span>

                        <SvgIcon
                            className="select__chevron"
                            icon="chevron-down"
                        />
                    </span>
                </button>
            </div>

            <DrawerBottom
                isOpen={isOpen}
                onClose={() => closeMenu()}
                heading={label}
            >
                <div className="select select--mobile select--open">
                    {/*
                        An invisible label for accessibility purposes.
                    */}
                    <label className="u-hide" {...getLabelProps()}>
                        {label}
                    </label>

                    {/*
                        An invisible dummy toggle button just so we're calling
                        getToggleButtonProps to ensure rc-drawer doesn't crash.
                    */}
                    <button
                        className="u-hide"
                        {...getToggleButtonProps()}
                    ></button>
                    <div className="select__options-wrapper">
                        <ul
                            {...getMenuProps()}
                            className={classNames('select__options-list', {
                                'u-hide': !isOpen
                            })}
                        >
                            {isOpen &&
                                items.map((item, index) => {
                                    const itemText = getItemText(item);
                                    const isSelected =
                                        getItemText(selectedItem) === itemText;

                                    return (
                                        <li
                                            key={`${itemText}${index}`}
                                            {...getItemProps({ item, index })}
                                            className={classNames(
                                                'select__option',
                                                {
                                                    'select__option--selected':
                                                        isSelected,
                                                    'select__option--highlighted':
                                                        highlightedIndex ===
                                                        index
                                                }
                                            )}
                                        >
                                            <span>{itemText}</span>

                                            {isSelected && (
                                                <SvgIcon
                                                    icon="checkmark"
                                                    className="select__option-button-icon"
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </div>
            </DrawerBottom>
        </>
    );
};

SelectMobile.propTypes = {
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func
};
